/** Pure helpers for the Kitchen Display mode (POS2-878). Kept free of React so
 * the range/target/format logic is unit-testable with a fixed `now`. */

import type { Locale } from "@/i18n/config";
import { formatDate } from "@/lib/format";
import { shiftDay, zonedDay, zonedDayEnd, zonedDayStart } from "@/lib/zoned-day";

/**
 * Yesterday in the SITE's zone — the kitchen wall shows the kitchen's yesterday, never
 * the viewer's (POS2-1039) — as the civil `day` plus its `[00:00:00.000, 23:59:59.999]`
 * instants. Matches how the day-anchored presets close their range (end-of-day .999), so
 * the stats and list endpoints bucket exactly the intended day.
 */
export function yesterdayRange(
  now: Date,
  timeZone: string,
): { day: string; from: string; to: string } {
  const day = shiftDay(zonedDay(now, timeZone), -1);
  return {
    day,
    from: zonedDayStart(day, timeZone).toISOString(),
    to: zonedDayEnd(day, timeZone).toISOString(),
  };
}

/**
 * The day's waste target in currency: the day's food-spend share times the
 * waste-% target. Null when either input is missing — the tile then renders a
 * neutral colour and the "set food spend" hint rather than a fabricated target.
 */
export function kitchenDailyTarget(
  daySpend: number | null,
  targetPct: number | null,
): number | null {
  if (daySpend == null || targetPct == null) return null;
  return (daySpend * targetPct) / 100;
}

/** A capture's clock time (HH:mm) in its own site timezone, for the photo overlay. */
export function formatCaptureTime(
  locale: Locale,
  loggedAt: string,
  timezone: string | null,
): string {
  return formatDate(locale, loggedAt, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone ?? "UTC",
  });
}
